// @givmo/fundraisers-module — organization fundraiser decode + display helpers (CPB-WEB1).
//
// ★ NUMBERS-ARE-THE-LAW. Every money/percent figure on a fundraiser is rendered EXACTLY as the backend
// returned it. The owner-portal read (`OrgFundraiserOut`) already attaches server-computed
// `stats.total_raised`, `goal_amount`, and `goal_progress_percent`; this module NEVER re-sums, re-derives,
// or recomputes a raised total or a progress percent from the parts. Like the §321 wire, this OWNER
// projection emits `total_raised` / `goal_amount` as exact 2-decimal money STRINGS in dollars (the repo's
// decimal-string money-wire convention — never a float, so no IEEE-754 drift) — so `formatFundraiserMoney`
// only *formats* the server string for display (via the api-client's string-only `formatMoneyDisplay`,
// never `parseFloat`/arithmetic), and the thermometer's bar width comes from the server
// `goal_progress_percent` (a plain 0–100 number — a bounded ratio, not a money amount), never `raised / goal`.
//
// STATE is likewise the backend's vocab-clean token (`draft`/`active`/`closed`/`deactivated`, D-CAMP-2 —
// `closed` wins over `active` when `closed_at` is set); we map it to a BADGE TONE (purely visual) and a
// human LABEL, and an unknown future token degrades to a neutral tone with a humanized label.

import { extractList } from '@givmo/api-client/list';
import { isGivmoError, type OrgFundraiserOut } from '@givmo/api-client';
import type { BadgeProps } from '@givmo/design-system';
import { formatMoneyDisplay } from '@givmo/api-client';

/** The Badge's tone union (the design-system doesn't re-export the tone type directly). */
type BadgeTone = NonNullable<BadgeProps['tone']>;

/**
 * Fundraisers from `GET /org/{org_ref}/fundraisers`. The endpoint returns the canonical
 * `{data,meta,links}` envelope, which the api-client middleware unwraps to a bare `OrgFundraiserOut[]`
 * at runtime — `extractList` reads the bare array, the un-unwrapped `{data}` envelope, or `[]` (never
 * throws), regardless of the statically-typed `OrgFundraiserPaginatedResponse`.
 */
export function extractFundraisers(body: unknown): OrgFundraiserOut[] {
  return extractList<OrgFundraiserOut>(body, 'data');
}

/**
 * Format a server money STRING (dollars) for display, prefixed with `$`, with cents only when there
 * are some (e.g. `"5240.5"` → `"$5,240.50"`, `"60000.00"` → `"$60,000"`, so a progress card reads
 * "$0 raised of $60,000 goal" — the EMC-WEB7 production finding).
 * MONEY LAW: the decimal wire string is handed straight to the string-only formatter — no `parseFloat`,
 * no arithmetic, no re-derivation. A null/undefined/empty value → an em dash (so a missing goal never
 * renders `$NaN`); a STRING value that isn't a plain decimal falls back to the raw wire string
 * verbatim (never silently drop or alter a money value). The wire contract is decimal STRINGS —
 * the former runtime-`number` tolerance was dropped with the shared string-only presenter.
 */
export function formatFundraiserMoney(value: string | null | undefined): string {
  if (value == null || value === '') return '—';
  const formatted = formatMoneyDisplay(value, { cents: 'when-present' });
  return formatted === null ? value : `$${formatted}`;
}

/** Format an ISO date/timestamp for display; tolerant of null/invalid values → em dash. */
export function displayDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Format a plain count (donation count) — an integer, shown verbatim; tolerant of null. */
export function displayCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return String(value);
}

/**
 * The pinned state → badge tone map (FCM-WEB24). Jonathan's portal ruling (FCM-WEB18 Q6, 2026-09-27)
 * reserves red for a Givmo takedown and makes every other ending grey, so every state here is grey
 * and only Active is green. The `/org` DTO's `state` has no takedown value: the red "Taken down"
 * reads off the record's `lifecycle.removed_by_givmo` instead ({@link fundraiserBadge}, FCM-WEB28).
 */
export const FUNDRAISER_STATE_TONES: Readonly<
  Record<'draft' | 'active' | 'closed' | 'deactivated', BadgeTone>
> = {
  draft: 'neutral',
  active: 'success',
  closed: 'neutral',
  deactivated: 'neutral',
};

/**
 * Badge tone for a vocab-clean fundraiser `state` token. PURELY VISUAL — the human text comes from
 * {@link fundraiserStateLabel}, never re-derived from this. An unknown future token → neutral.
 */
export function fundraiserStateTone(state: string | null | undefined): BadgeTone {
  const key = (state ?? '').toLowerCase();
  return Object.hasOwn(FUNDRAISER_STATE_TONES, key)
    ? FUNDRAISER_STATE_TONES[key as keyof typeof FUNDRAISER_STATE_TONES]
    : 'neutral';
}

/** Human label for a fundraiser `state` token (tolerant of unknown future values → humanized). */
export function fundraiserStateLabel(state: string | null | undefined): string {
  switch ((state ?? '').toLowerCase()) {
    case 'draft':
      return 'Draft';
    case 'active':
      return 'Active';
    case 'closed':
      return 'Closed';
    case 'deactivated':
      return 'Deactivated';
    default: {
      const cleaned = (state ?? '').replace(/[_-]+/g, ' ').trim().toLowerCase();
      if (cleaned.length === 0) return '—';
      return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    }
  }
}

/** True when the fundraiser is a DRAFT (unpublished, publicly invisible) — drives the publish affordance. */
export function isDraftState(state: string | null | undefined): boolean {
  return (state ?? '').toLowerCase() === 'draft';
}

/** True when the fundraiser is ACTIVE (published + donatable) — drives close/share affordances. */
export function isActiveState(state: string | null | undefined): boolean {
  return (state ?? '').toLowerCase() === 'active';
}

/** True when the fundraiser is CLOSED (`closed_at` set) — the editor renders read-only with final stats. */
export function isClosedState(state: string | null | undefined): boolean {
  return (state ?? '').toLowerCase() === 'closed';
}

/**
 * The thermometer bar width (0–100) from the server's `goal_progress_percent`. Returns `null` when there
 * is no goal/percent (the thermometer hides its bar). The value is CLAMPED to [0,100] for the CSS width
 * ONLY — the underlying number is the server's, never re-derived from raised/goal.
 */
export function goalPercentBarWidth(percent: number | null | undefined): number | null {
  if (percent == null || !Number.isFinite(percent)) return null;
  return Math.min(100, Math.max(0, percent));
}

/**
 * Display string for the server `goal_progress_percent` (e.g. `"48%"`). Rounded for the label ONLY — a
 * display concern, not a recomputation (percent is not money). Returns `null` when there's no goal.
 */
export function formatGoalPercent(percent: number | null | undefined): string | null {
  const width = goalPercentBarWidth(percent);
  if (width == null) return null;
  return `${Math.round(width)}%`;
}

// ── Ending a fundraiser: the `/org` record's lifecycle (FCM-WEB28; plan §FC-15, D-FCM-24) ──────────

/**
 * The `/org` record's `lifecycle` hints (FCM-BE39): which lifecycle acts the `/org` door would accept
 * from THIS caller on THIS fundraiser — the caller's WRITE_FUNDRAISERS standing in for the owner — and
 * whether Givmo took it down (`removed_by_givmo`). The snapshot types the object, and it is still read
 * defensively: a missing or malformed object (a backend before FCM-BE39) is `null`, and each flag is
 * true only for a literal `true`, so a value this build does not understand never offers an act.
 */
export interface OrgLifecycleHints {
  canClose: boolean;
  canReopen: boolean;
  canDeactivate: boolean;
  canReactivate: boolean;
  canDelete: boolean;
  removedByGivmo: boolean;
  takedownLocked: boolean;
}

export function orgLifecycleHints(row: OrgFundraiserOut): OrgLifecycleHints | null {
  const raw: unknown = row.lifecycle;
  if (raw === null || typeof raw !== 'object') return null;
  const hints = raw as Record<string, unknown>;
  const flag = (key: string) => hints[key] === true;
  return {
    canClose: flag('can_close'),
    canReopen: flag('can_reopen'),
    canDeactivate: flag('can_deactivate'),
    canReactivate: flag('can_reactivate'),
    canDelete: flag('can_delete'),
    removedByGivmo: flag('removed_by_givmo'),
    takedownLocked: flag('takedown_locked'),
  };
}

/** A Givmo takedown: a deactivated record the server reports `removed_by_givmo`. */
export function isTakenDown(row: OrgFundraiserOut): boolean {
  return (
    (row.state ?? '').toLowerCase() === 'deactivated' &&
    orgLifecycleHints(row)?.removedByGivmo === true
  );
}

/**
 * The state badge (rulings Q3 and Q6): "Taken down" in red for a Givmo takedown, the one ending the
 * organization did not choose; every other state keeps the pinned tones (FCM-WEB24).
 */
export function fundraiserBadge(row: OrgFundraiserOut): { label: string; tone: BadgeTone } {
  if (isTakenDown(row)) return { label: 'Taken down', tone: 'danger' };
  return { label: fundraiserStateLabel(row.state), tone: fundraiserStateTone(row.state) };
}

/**
 * Which ways to end, or to undo an ending, the page offers — each exactly when the server's hint says
 * the `/org` door would accept it (FCM-WEB28, "driven only by the server's lifecycle hints").
 * `contactSupport` is the funded "Need it taken down?" card: `takedown_locked`, never inferred from
 * `can_deactivate`. Without hints (`hinted` false, a backend before FCM-BE39) the page keeps its own
 * Close and Reopen rules (the state alone) and offers none of the other acts.
 */
export interface FundraiserEndings {
  hinted: boolean;
  close: boolean;
  reopen: boolean;
  deactivate: boolean;
  reactivate: boolean;
  deleteDraft: boolean;
  deleteForever: boolean;
  contactSupport: boolean;
  takenDown: boolean;
}

export function fundraiserEndings(row: OrgFundraiserOut): FundraiserEndings {
  const hints = orgLifecycleHints(row);
  const state = (row.state ?? '').toLowerCase();
  if (hints === null)
    return {
      hinted: false,
      close: state === 'active',
      reopen: state === 'closed',
      deactivate: false,
      reactivate: false,
      deleteDraft: false,
      deleteForever: false,
      contactSupport: false,
      takenDown: false,
    };
  return {
    hinted: true,
    close: hints.canClose,
    reopen: hints.canReopen,
    deactivate: hints.canDeactivate,
    reactivate: hints.canReactivate,
    deleteDraft: hints.canDelete && state === 'draft',
    deleteForever: hints.canDelete && state === 'deactivated',
    contactSupport: hints.takedownLocked,
    takenDown: isTakenDown(row),
  };
}

/** Every support line names this address (house rule; plan §FC-15's Contact support lines). */
export const SUPPORT_EMAIL = 'support@givmocharitable.org';

function supportMailto(subject: string): string {
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`;
}

/** The funded card's mailto: "Take down: {name} ({id})" (ruling Q4). */
export function takedownSupportHref(row: OrgFundraiserOut): string {
  return supportMailto(`Take down: ${row.name} (${row.id})`);
}

/** The takedown line's mailto: "Question about a takedown: {name} ({id})" (ruling A8). */
export function takedownQuestionHref(row: OrgFundraiserOut): string {
  return supportMailto(`Question about a takedown: ${row.name} (${row.id})`);
}

/**
 * The lifecycle lines Jonathan ruled for the employer portal (FCM-WEB18 Q1–Q9 and A8, 2026-09-27),
 * verbatim from the portal as built (`apps/admin/.../company-fundraisers-decode.ts`, `LIFECYCLE_COPY`),
 * which FCM-WEB28's mockups carried into this module (ruled 2026-10-05). Only the lines this module
 * shows; its own Close and Reopen words stay in the editor.
 */
export const LIFECYCLE_COPY = {
  deactivateCardTitle: 'Deactivate fundraiser',
  deactivateCardBody: 'Hides the fundraiser from donors. You can reactivate it later.',
  deactivateTitle: 'Deactivate this fundraiser?',
  deactivateBody:
    'Deactivating hides this fundraiser’s page and stops new donations. Donations already made are unaffected. You can reactivate it later.',
  deactivateConfirm: 'Deactivate',
  deleteDraftCardTitle: 'Delete draft',
  deleteDraftCardBody: 'Deletes this draft for good.',
  deleteDraftTitle: 'Delete this draft?',
  deleteDraftConfirm: 'Delete draft',
  deleteForeverCardTitle: 'Delete forever',
  deleteForeverCardBody: 'Deletes this fundraiser for good.',
  deleteForeverTitle: 'Delete this fundraiser?',
  deleteForeverConfirm: 'Delete fundraiser',
  cantBeUndone: 'This can’t be undone.',
  deactivatedBanner: 'This fundraiser is deactivated, so its page is hidden.',
  reactivate: 'Reactivate',
  takedownLead: 'Givmo took this fundraiser down.',
  takedownLink: 'Contact support',
  takedownTail: 'with questions.',
  fundedTitle: 'Need it taken down?',
  fundedBody: 'Once a fundraiser has donations, only Givmo can take it down.',
  fundedAction: 'Contact support',
  takenDownDetails: 'This fundraiser can’t be edited while it’s taken down.',
  // A page a Close froze before it was deactivated: Reactivate returns it to Closed, and only Reopen
  // clears the freeze (FCM-WEB28; Jonathan approved both lines 2026-10-06, for this module and the
  // employer portal alike).
  frozenDeactivatedDetails:
    'Reactivate and then reopen the fundraiser to edit its name, description, or goal.',
  frozenDeactivatedRecipients:
    'Reactivate and then reopen the fundraiser to change its recipients.',
} as const;

/**
 * The lifecycle toasts, the approved lines with their full stops (ruling 5, 2026-10-05): the four new
 * acts', and the module's own Close and Reopen in the same form.
 */
export const LIFECYCLE_TOASTS = {
  closed: 'Fundraiser closed.',
  reopened: 'Fundraiser reopened.',
  deactivated: 'Fundraiser deactivated.',
  reactivated: 'Fundraiser reactivated.',
  draftDeleted: 'Draft deleted.',
  deleted: 'Fundraiser deleted.',
} as const;

/**
 * A refused lifecycle act, in the server's own words. On the `/org` door one typed code carries a
 * different sentence per act — `FUNDRAISER_HAS_DONATIONS` is the Deactivate sentence on Deactivate and
 * `FUNDED_DELETE_DETAIL` on Delete, and Gamified Fundraisers' GFU-BE26 answers a pledge host in pledge
 * words — so a line keyed on the code alone would say the wrong thing; the page shows the server's.
 * `stale` is any 409: the fundraiser moved under the page (a first gift, a takedown, a change made
 * elsewhere), so the page re-reads and offers only what the server now accepts.
 */
export function lifecycleRefusal(
  error: unknown,
  fallback: string,
): { message: string; stale: boolean } {
  if (!isGivmoError(error)) return { message: fallback, stale: false };
  return { message: error.message || fallback, stale: error.status === 409 };
}
